import React, { useMemo, useState } from 'react';
import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    IconButton,
    TextField,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import BoltIcon from '@mui/icons-material/Bolt';
import CheckIcon from '@mui/icons-material/Check';
import AddIcon from '@mui/icons-material/Add';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import VerifiedIcon from '@mui/icons-material/Verified';
import { OBSIDIAN, OBSIDIAN_BODY_FONT, OBSIDIAN_DISPLAY_FONT } from '../../constants/obsidianDashboardTheme';
import { createSandboxCourse, updateSandboxCourse, uploadSandboxCoverImage } from '../../redux/sandbox/actions';
import { showToastMessage, useDispatchPromise } from '../../utils/AppUtils';
import { SandboxCoverImagePicker } from './SandboxCoverImagePicker';
import { RichTextEditor } from '../Common/RichTextEditor';
import { SandboxCourseBreakdownEditor } from './SandboxCourseBreakdownEditor';
import { extractSandboxModulesFromBreakdown, type SandboxBreakdownClass } from '../../utils/sandboxCurriculum';

const FOCUS_TAGS = ['Cinematic UGC', 'Lighting & Sound', 'Monetization', 'Viral Hooks', 'AI Workflows'];
const TIERS = [
    { id: 'beginner', label: 'Beginner' },
    { id: 'intermediate', label: 'Intermediate' },
    { id: 'masterclass', label: 'Masterclass' },
] as const;

export type SandboxCreateCourseResult = {
    courseId: string;
    openClasses: boolean;
};

type Props = {
    open: boolean;
    onClose: () => void;
    onCreated: (result: SandboxCreateCourseResult) => void;
};

export function SandboxCreateCourseModal({ open, onClose, onCreated }: Props) {
    const dispatchPromise = useDispatchPromise();
    const [title, setTitle] = useState('');
    const [categories, setCategories] = useState<string[]>(['Cinematic UGC', 'Lighting & Sound']);
    const [masteryTier, setMasteryTier] = useState<'beginner' | 'intermediate' | 'masterclass'>('masterclass');
    const [coverPreview, setCoverPreview] = useState('');
    const [coverFile, setCoverFile] = useState<File | null>(null);
    const [description, setDescription] = useState('');
    const [breakdown, setBreakdown] = useState<SandboxBreakdownClass[]>([]);
    const [officeHours, setOfficeHours] = useState(false);
    const [pricingMode, setPricingMode] = useState<'pro_pass' | 'standalone'>('pro_pass');
    const [priceDollars, setPriceDollars] = useState('149');
    const [saving, setSaving] = useState(false);
    const [customTagOpen, setCustomTagOpen] = useState(false);
    const [customTagDraft, setCustomTagDraft] = useState('');

    const charCount = title.length;
    const customTags = categories.filter(t => !FOCUS_TAGS.includes(t));

    const fieldSx = useMemo(
        () => ({
            '& .MuiOutlinedInput-root': {
                bgcolor: '#323232',
                color: OBSIDIAN.onSurface,
                fontFamily: OBSIDIAN_BODY_FONT,
                fontSize: 14,
                borderRadius: 2,
                '& fieldset': { borderColor: `${OBSIDIAN.outline}55` },
                '&:hover fieldset': { borderColor: OBSIDIAN.outline },
                '&.Mui-focused fieldset': { borderColor: OBSIDIAN.primary },
            },
            '& .MuiInputBase-input': {
                color: OBSIDIAN.onSurface,
                '&::placeholder': { color: OBSIDIAN.onSurfaceVariant, opacity: 1 },
            },
        }),
        [],
    );

    const reset = () => {
        setTitle('');
        setCategories(['Cinematic UGC', 'Lighting & Sound']);
        setMasteryTier('masterclass');
        setCoverPreview('');
        setCoverFile(null);
        setDescription('');
        setBreakdown([]);
        setOfficeHours(false);
        setPricingMode('pro_pass');
        setPriceDollars('149');
        setCustomTagOpen(false);
        setCustomTagDraft('');
    };

    const toggleTag = (tag: string) => {
        setCategories(prev => (prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]));
    };

    const addCustomTag = () => {
        const tag = customTagDraft.trim().slice(0, 40);
        if (!tag) return;
        setCategories(prev => (prev.includes(tag) ? prev : [...prev, tag]));
        setCustomTagDraft('');
        setCustomTagOpen(false);
    };

    const submit = async (openClasses: boolean) => {
        if (!title.trim()) {
            showToastMessage('Class title is required', 'error');
            return;
        }
        if (!categories.length) {
            showToastMessage('Pick at least one focus tag', 'error');
            return;
        }
        if (breakdown.some(row => !row.title.trim() && (row.description || '').trim())) {
            showToastMessage('Every class in the breakdown needs a title', 'error');
            return;
        }
        setSaving(true);
        try {
            const cents =
                pricingMode === 'pro_pass' ? 0 : Math.max(0, Math.round(parseFloat(priceDollars || '0') * 100));
            const modules = extractSandboxModulesFromBreakdown(breakdown);
            const res: any = await dispatchPromise(
                createSandboxCourse({
                    title: title.trim().slice(0, 75),
                    description: description.trim(),
                    cover_image: null,
                    categories,
                    mastery_tier: masteryTier,
                    pricing_mode: pricingMode,
                    office_hours_enabled: officeHours,
                    price_cents: cents,
                    with_subscriber_discount: true,
                    subscriber_discount: 20,
                    is_free_for_subscriber: pricingMode === 'pro_pass',
                    modules: modules as any,
                } as any),
            );
            const course = res?.data?.course || res?.course;
            const courseId = course?._id;
            if (!courseId) throw new Error('Course created but id missing');

            if (coverFile) {
                try {
                    const coverPath = (await dispatchPromise(
                        uploadSandboxCoverImage({ file: coverFile, courseId }),
                    )) as string;
                    await dispatchPromise(updateSandboxCourse({ courseId, body: { cover_image: coverPath } }));
                } catch (coverErr: any) {
                    showToastMessage(
                        coverErr?.message || 'Course saved, but cover upload failed — edit the course to retry',
                        'error',
                    );
                }
            }

            showToastMessage(openClasses ? 'Course saved — add your classes' : 'Draft course saved', 'success');
            reset();
            onCreated({ courseId, openClasses });
            onClose();
        } catch (e: any) {
            showToastMessage(e?.message || 'Could not create course', 'error');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="md"
            scroll="paper"
            PaperProps={{
                sx: {
                    bgcolor: OBSIDIAN.surfaceContainerLow,
                    backgroundImage: 'none',
                    borderRadius: 3,
                    boxShadow: '0 20px 50px rgba(0,0,0,0.85)',
                    m: { xs: 1.5, sm: 2 },
                    maxHeight: 'calc(100dvh - 32px)',
                    width: '100%',
                },
            }}
            slotProps={{
                backdrop: { sx: { bgcolor: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)' } },
            }}>
            <Box sx={{ bgcolor: OBSIDIAN.surfaceContainer, px: 3, pt: 3, pb: 2.5, flexShrink: 0 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                    <Box
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 1,
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 999,
                            bgcolor: OBSIDIAN.surfaceContainerHighest,
                            color: OBSIDIAN.primary,
                            fontFamily: OBSIDIAN_BODY_FONT,
                            fontSize: 11,
                            fontWeight: 700,
                            letterSpacing: '0.08em',
                            textTransform: 'uppercase',
                        }}>
                        <BoltIcon sx={{ fontSize: 14 }} />
                        Curriculum Builder · Step 1 of 3
                    </Box>
                    <IconButton
                        onClick={onClose}
                        size="small"
                        sx={{ bgcolor: OBSIDIAN.surfaceContainerHighest, color: OBSIDIAN.onSurfaceVariant }}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>
                <Typography
                    sx={{
                        fontFamily: OBSIDIAN_DISPLAY_FONT,
                        fontWeight: 800,
                        fontSize: { xs: 20, sm: 24 },
                        color: OBSIDIAN.onSurface,
                    }}>
                    Create New Masterclass
                </Typography>
                <Typography
                    sx={{ fontFamily: OBSIDIAN_BODY_FONT, fontSize: 13, color: OBSIDIAN.onSurfaceVariant, mt: 0.5 }}>
                    Launch a course students buy once. Add classes under it — some can be free preview, others unlock
                    after purchase.
                </Typography>
                <Box sx={{ mt: 2, height: 4, borderRadius: 999, bgcolor: OBSIDIAN.surfaceVariant, overflow: 'hidden' }}>
                    <Box
                        sx={{
                            width: '33%',
                            height: '100%',
                            background: `linear-gradient(90deg, ${OBSIDIAN.primary}, ${OBSIDIAN.secondary})`,
                        }}
                    />
                </Box>
            </Box>

            <DialogContent
                dividers
                sx={{
                    p: 3,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 3.5,
                    bgcolor: OBSIDIAN.surfaceContainerLow,
                    borderColor: OBSIDIAN.surfaceContainerHighest,
                    // Explicit height so overflow scrolls (flex+maxHeight alone was clipping)
                    maxHeight: { xs: 'calc(100dvh - 240px)', sm: 'calc(100dvh - 260px)' },
                    overflowY: 'auto',
                    overscrollBehavior: 'contain',
                    WebkitOverflowScrolling: 'touch',
                }}>
                <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography
                            sx={{
                                fontSize: 11,
                                fontWeight: 700,
                                letterSpacing: '0.08em',
                                textTransform: 'uppercase',
                                color: OBSIDIAN.primary,
                                fontFamily: OBSIDIAN_BODY_FONT,
                            }}>
                            Course Title *
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: OBSIDIAN.onSurfaceVariant }}>{charCount}/75</Typography>
                    </Box>
                    <TextField
                        fullWidth
                        placeholder="e.g., Masterclass in Editorial Lighting & Sound"
                        value={title}
                        onChange={e => setTitle(e.target.value.slice(0, 75))}
                        inputProps={{ maxLength: 75 }}
                        sx={fieldSx}
                    />
                    <Typography
                        sx={{
                            mt: 2,
                            mb: 1,
                            fontSize: 11,
                            fontWeight: 700,
                            letterSpacing: '0.08em',
                            textTransform: 'uppercase',
                            color: OBSIDIAN.primary,
                            fontFamily: OBSIDIAN_BODY_FONT,
                        }}>
                        Overview description
                    </Typography>
                    <Typography
                        sx={{ mb: 1, fontSize: 12, color: OBSIDIAN.onSurfaceVariant, fontFamily: OBSIDIAN_BODY_FONT }}>
                        Shown on the course page and in the class Overview tab.
                    </Typography>
                    <RichTextEditor
                        value={description}
                        onChange={setDescription}
                        placeholder="What students will learn, who it’s for, and why this course matters…"
                        shellVariant="companyAbout"
                        compact
                    />
                    <Typography
                        sx={{
                            mt: 2,
                            mb: 1,
                            fontSize: 11,
                            fontWeight: 600,
                            letterSpacing: '0.08em',
                            textTransform: 'uppercase',
                            color: OBSIDIAN.onSurfaceVariant,
                        }}>
                        Curriculum Focus Tags *
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {[...FOCUS_TAGS, ...customTags].map(tag => {
                            const active = categories.includes(tag);
                            return (
                                <Box
                                    key={tag}
                                    component="button"
                                    type="button"
                                    onClick={() => toggleTag(tag)}
                                    sx={{
                                        border: 'none',
                                        cursor: 'pointer',
                                        px: 1.5,
                                        py: 0.75,
                                        borderRadius: 999,
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        bgcolor: active ? OBSIDIAN.primary : OBSIDIAN.surfaceContainerHigh,
                                        color: active ? OBSIDIAN.onPrimaryFixed : OBSIDIAN.onSurfaceVariant,
                                        fontFamily: OBSIDIAN_BODY_FONT,
                                        fontSize: 12,
                                        fontWeight: active ? 700 : 500,
                                    }}>
                                    {active && <CheckIcon sx={{ fontSize: 14 }} />}
                                    {tag}
                                </Box>
                            );
                        })}
                        <Box
                            component="button"
                            type="button"
                            onClick={() => setCustomTagOpen(v => !v)}
                            sx={{
                                border: customTagOpen ? `1px solid ${OBSIDIAN.primary}66` : '1px solid transparent',
                                cursor: 'pointer',
                                px: 1.5,
                                py: 0.75,
                                borderRadius: 999,
                                bgcolor: customTagOpen ? OBSIDIAN.surfaceContainerHighest : OBSIDIAN.surfaceContainer,
                                color: customTagOpen ? OBSIDIAN.primary : OBSIDIAN.onSurfaceVariant,
                                fontFamily: OBSIDIAN_BODY_FONT,
                                fontSize: 12,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.5,
                            }}>
                            <AddIcon sx={{ fontSize: 14 }} /> Custom
                        </Box>
                    </Box>
                    {customTagOpen && (
                        <Box
                            sx={{
                                mt: 1.25,
                                p: 1.25,
                                borderRadius: 2,
                                bgcolor: OBSIDIAN.surfaceContainerHighest,
                                display: 'flex',
                                gap: 1,
                                alignItems: 'center',
                            }}>
                            <TextField
                                autoFocus
                                fullWidth
                                size="small"
                                placeholder="Custom focus tag"
                                value={customTagDraft}
                                onChange={e => setCustomTagDraft(e.target.value.slice(0, 40))}
                                onKeyDown={e => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        addCustomTag();
                                    }
                                    if (e.key === 'Escape') {
                                        setCustomTagOpen(false);
                                        setCustomTagDraft('');
                                    }
                                }}
                                sx={fieldSx}
                            />
                            <Button
                                onClick={addCustomTag}
                                disabled={!customTagDraft.trim()}
                                sx={{
                                    flexShrink: 0,
                                    px: 1.75,
                                    py: 0.85,
                                    borderRadius: 999,
                                    background: `linear-gradient(90deg, ${OBSIDIAN.primary}, #feb700)`,
                                    color: OBSIDIAN.onPrimaryFixed,
                                    textTransform: 'none',
                                    fontWeight: 700,
                                    fontSize: 12,
                                    fontFamily: OBSIDIAN_DISPLAY_FONT,
                                    '&.Mui-disabled': { opacity: 0.4, color: OBSIDIAN.onPrimaryFixed },
                                }}>
                                Add
                            </Button>
                        </Box>
                    )}

                    <Typography
                        sx={{
                            mt: 2,
                            mb: 1,
                            fontSize: 11,
                            fontWeight: 600,
                            letterSpacing: '0.08em',
                            textTransform: 'uppercase',
                            color: OBSIDIAN.onSurfaceVariant,
                        }}>
                        Mastery Tier
                    </Typography>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr 1fr',
                            gap: 0.5,
                            p: 0.5,
                            borderRadius: 2,
                            bgcolor: OBSIDIAN.surfaceContainerHighest,
                        }}>
                        {TIERS.map(t => (
                            <Box
                                key={t.id}
                                component="button"
                                type="button"
                                onClick={() => setMasteryTier(t.id)}
                                sx={{
                                    border: 'none',
                                    cursor: 'pointer',
                                    py: 1,
                                    borderRadius: 1.5,
                                    bgcolor: masteryTier === t.id ? OBSIDIAN.surfaceContainer : 'transparent',
                                    color: masteryTier === t.id ? OBSIDIAN.primary : OBSIDIAN.onSurfaceVariant,
                                    fontFamily: OBSIDIAN_BODY_FONT,
                                    fontSize: 12,
                                    fontWeight: masteryTier === t.id ? 700 : 500,
                                }}>
                                {t.label}
                            </Box>
                        ))}
                    </Box>
                </Box>

                <Box>
                    <Typography
                        sx={{
                            mb: 1,
                            fontSize: 11,
                            fontWeight: 700,
                            letterSpacing: '0.08em',
                            textTransform: 'uppercase',
                            color: OBSIDIAN.primary,
                        }}>
                        Cover image
                    </Typography>
                    <SandboxCoverImagePicker
                        previewUrl={coverPreview}
                        onCropped={(preview, file) => {
                            setCoverPreview(preview);
                            setCoverFile(file);
                        }}
                        onClear={() => {
                            setCoverPreview('');
                            setCoverFile(null);
                        }}
                    />
                </Box>

                <SandboxCourseBreakdownEditor classes={breakdown} onChange={setBreakdown} />

                <Box
                    component="label"
                    sx={{
                        display: 'flex',
                        gap: 1.5,
                        p: 1.75,
                        borderRadius: 3,
                        bgcolor: OBSIDIAN.surfaceContainer,
                        cursor: 'pointer',
                    }}>
                    <Checkbox
                        checked={officeHours}
                        onChange={e => setOfficeHours(e.target.checked)}
                        sx={{ color: OBSIDIAN.outline, '&.Mui-checked': { color: OBSIDIAN.primary }, p: 0.5 }}
                    />
                    <Box>
                        <Typography
                            sx={{
                                fontFamily: OBSIDIAN_DISPLAY_FONT,
                                fontWeight: 700,
                                fontSize: 12,
                                color: OBSIDIAN.onSurface,
                            }}>
                            Live Sandbox Office Hours
                        </Typography>
                        <Typography
                            sx={{
                                fontFamily: OBSIDIAN_BODY_FONT,
                                fontSize: 11,
                                color: OBSIDIAN.onSurfaceVariant,
                            }}>
                            Marks this course as offering office hours. Also set weekly slots + Google Calendar in
                            Studio → Office hours.
                        </Typography>
                    </Box>
                </Box>

                <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                        <Typography
                            sx={{
                                fontSize: 11,
                                fontWeight: 700,
                                letterSpacing: '0.08em',
                                textTransform: 'uppercase',
                                color: OBSIDIAN.primary,
                            }}>
                            Monetization · Per Course
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: OBSIDIAN.onSurfaceVariant }}>
                            Classes unlock after purchase
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
                        <Box
                            onClick={() => setPricingMode('pro_pass')}
                            sx={{
                                p: 2,
                                borderRadius: 3,
                                bgcolor:
                                    pricingMode === 'pro_pass'
                                        ? OBSIDIAN.surfaceContainerHighest
                                        : OBSIDIAN.surfaceContainer,
                                cursor: 'pointer',
                                position: 'relative',
                                border:
                                    pricingMode === 'pro_pass'
                                        ? `1px solid ${OBSIDIAN.primary}55`
                                        : '1px solid transparent',
                            }}>
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: -10,
                                    right: 12,
                                    px: 1,
                                    py: 0.25,
                                    borderRadius: 999,
                                    bgcolor: OBSIDIAN.secondary,
                                    color: '#004734',
                                    fontSize: 9,
                                    fontWeight: 800,
                                }}>
                                SUBSCRIBER ACCESS
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Box
                                    sx={{
                                        width: 16,
                                        height: 16,
                                        borderRadius: '50%',
                                        bgcolor:
                                            pricingMode === 'pro_pass'
                                                ? OBSIDIAN.primary
                                                : OBSIDIAN.surfaceContainerHighest,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}>
                                    {pricingMode === 'pro_pass' && (
                                        <CheckIcon sx={{ fontSize: 12, color: OBSIDIAN.onPrimaryFixed }} />
                                    )}
                                </Box>
                                <Typography
                                    sx={{
                                        fontFamily: OBSIDIAN_DISPLAY_FONT,
                                        fontWeight: 700,
                                        fontSize: 12,
                                        color: OBSIDIAN.onSurface,
                                    }}>
                                    Included in Pro Pass
                                </Typography>
                            </Box>
                            <Typography
                                sx={{ fontSize: 11, color: OBSIDIAN.onSurfaceVariant, fontFamily: OBSIDIAN_BODY_FONT }}>
                                Free for platform subscribers. You earn from the subscriber pool.
                            </Typography>
                        </Box>
                        <Box
                            onClick={() => setPricingMode('standalone')}
                            sx={{
                                p: 2,
                                borderRadius: 3,
                                bgcolor:
                                    pricingMode === 'standalone'
                                        ? OBSIDIAN.surfaceContainerHighest
                                        : OBSIDIAN.surfaceContainer,
                                cursor: 'pointer',
                                opacity: pricingMode === 'standalone' ? 1 : 0.85,
                                border:
                                    pricingMode === 'standalone'
                                        ? `1px solid ${OBSIDIAN.primary}55`
                                        : '1px solid transparent',
                            }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Box
                                    sx={{
                                        width: 16,
                                        height: 16,
                                        borderRadius: '50%',
                                        bgcolor:
                                            pricingMode === 'standalone'
                                                ? OBSIDIAN.primary
                                                : OBSIDIAN.surfaceContainerHighest,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}>
                                    {pricingMode === 'standalone' && (
                                        <CheckIcon sx={{ fontSize: 12, color: OBSIDIAN.onPrimaryFixed }} />
                                    )}
                                </Box>
                                <Typography
                                    sx={{
                                        fontFamily: OBSIDIAN_DISPLAY_FONT,
                                        fontWeight: 700,
                                        fontSize: 12,
                                        color: OBSIDIAN.onSurface,
                                    }}>
                                    Standalone Buy
                                </Typography>
                            </Box>
                            <Typography
                                sx={{
                                    fontSize: 11,
                                    color: OBSIDIAN.onSurfaceVariant,
                                    mb: 1,
                                    fontFamily: OBSIDIAN_BODY_FONT,
                                }}>
                                One course purchase unlocks every paid class inside.
                            </Typography>
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    bgcolor: '#000',
                                    p: 1,
                                    borderRadius: 2,
                                }}>
                                <TextField
                                    size="small"
                                    value={priceDollars}
                                    onChange={e => setPriceDollars(e.target.value)}
                                    onClick={e => e.stopPropagation()}
                                    InputProps={{
                                        startAdornment: (
                                            <Typography sx={{ color: OBSIDIAN.primary, mr: 0.5, fontWeight: 700 }}>
                                                $
                                            </Typography>
                                        ),
                                    }}
                                    sx={{
                                        width: 110,
                                        '& .MuiOutlinedInput-root': {
                                            color: OBSIDIAN.primary,
                                            fontWeight: 700,
                                            '& fieldset': { border: 'none' },
                                        },
                                    }}
                                />
                                <Typography sx={{ fontSize: 10, color: OBSIDIAN.onSurfaceVariant }}>
                                    85% rev share
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                    <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 0.75, color: OBSIDIAN.secondary }}>
                        <VerifiedIcon sx={{ fontSize: 14 }} />
                        <Typography sx={{ fontSize: 11, fontFamily: OBSIDIAN_BODY_FONT }}>
                            Payment is per course. Mark individual classes as free preview when you add them.
                        </Typography>
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions
                sx={{
                    display: 'flex',
                    gap: 1.5,
                    px: 3,
                    py: 2,
                    m: 0,
                    flexShrink: 0,
                    borderTop: `1px solid ${OBSIDIAN.surfaceContainerHighest}`,
                    bgcolor: OBSIDIAN.surfaceContainerLow,
                }}>
                <Button
                    disabled={saving}
                    onClick={() => submit(false)}
                    sx={{
                        px: 2.5,
                        py: 1.25,
                        borderRadius: 999,
                        bgcolor: OBSIDIAN.surfaceContainerHighest,
                        color: OBSIDIAN.onSurfaceVariant,
                        textTransform: 'none',
                        fontWeight: 700,
                        fontFamily: OBSIDIAN_BODY_FONT,
                        fontSize: 12,
                    }}>
                    Save as Draft
                </Button>
                <Button
                    disabled={saving}
                    onClick={() => submit(true)}
                    endIcon={<ArrowForwardIcon />}
                    sx={{
                        flex: 1,
                        py: 1.25,
                        borderRadius: 999,
                        background: `linear-gradient(90deg, ${OBSIDIAN.primary}, #feb700)`,
                        color: OBSIDIAN.onPrimaryFixed,
                        textTransform: 'none',
                        fontWeight: 800,
                        fontFamily: OBSIDIAN_DISPLAY_FONT,
                        fontSize: 13,
                        boxShadow: '0 4px 20px rgba(255,201,101,0.25)',
                    }}>
                    {saving ? 'Saving…' : 'Save & Add Classes'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
